import axios from "axios";
import { postToNewebpay } from "@/utils/newebpay";
// 1. 必須解開註解並引入 router
import router from "@/router";
// 2. 必須引入 store 以便呼叫 actions
import store from "@/store";

const normalApiBaseUrl = process.env.VUE_APP_MAIN_API_BASE_URL;

const getAuthorizationHeader = (token, additionalHeaders = {}) => {
  if (token && token !== "null" && token !== "undefined" && token.trim() !== "") {
    return {
      ...additionalHeaders,
      Authorization: "Bearer " + token,
    };
  }
  return additionalHeaders;
};

// 將後端各種格式的錯誤 → 轉成字串訊息
const extractErrMsg = (error) => {
  // AxiosError
  const res = error?.response;
  if (res) {
    // 常見格式：{ message: "xxx" }、{ error: "xxx" } 或字串
    const data = res.data;
    if (typeof data === "string") return data;
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    return `伺服器錯誤 (${res.status})`;
  }
  // 網路/中斷
  if (error?.message === "Network Error") return "網路連線異常，請稍後再試。";
  return error?.message || "發生未知錯誤，請稍後再試。";
};

// 【無攔截器的專用認證實體】
// Token_SilentSignIn 和 Token_LogOut 必須使用此實體，
// 避免請求攔截器等待 syncPromise 造成死鎖 (deadlock)
const authRequest = axios.create({
  baseURL: normalApiBaseUrl,
  withCredentials: true,
});

const publicRequest = axios.create({
  baseURL: normalApiBaseUrl,
  withCredentials: true,
});

authRequest.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("_token");
    config.headers = getAuthorizationHeader(token);
    return config;
  },
  (error) => Promise.reject(error)
);

const createAxiosInstance = (headers) => {
  const instance = axios.create({
    baseURL: normalApiBaseUrl,
    headers,
    withCredentials: true,
  });

  instance.interceptors.request.use(
    async (config) => {
      // 【同步鎖等待】：如果目前正在執行靜默登入(SilentSignIn)，則等待其完成
      // 注意：Token_SilentSignIn 使用 authRequest，因此不會進入此攔截器，不會死鎖
      const { syncPromise } = require("@/store");
      if (syncPromise) {
        console.log("[Request] Waiting for active syncPromise...");
        await syncPromise;
      }

      const token = localStorage.getItem("_token");
      config.headers = getAuthorizationHeader(token, headers);
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => {
      const newAccessToken = response.headers["new_access_token"];
      if (newAccessToken) {
        localStorage.setItem("_token", newAccessToken);
      }
      return response;
    },
    async (error) => {
      const config = error.config;
      
      // 3. 處理 401 Unauthorized (Token 失效、RefreshToken 已使用、並發衝突等)
      if (error.response && error.response.status === 401 && config) {
        // 如果這個請求已經是重試過了，就不再重試，直接走跳轉邏輯
        if (config._isRetry) {
           return finalizeUnauthorized(error);
        }
        config._isRetry = true;

        // 這個請求送出之後 token 已經換過了（同一頁的另一個請求、或別的分頁換到新票）：直接用新的重試，不用再換一次
        const sentToken = String((config.headers && config.headers.Authorization) || "").replace(/^Bearer\s+/, "");
        const currentToken = localStorage.getItem("_token");
        if (currentToken && sentToken && currentToken !== sentToken) {
          config.headers.Authorization = "Bearer " + currentToken;
          return instance(config);
        }

        console.warn("[Response] 偵測到 401，嘗試靜默刷新並重試請求:", config.url);

        try {
          // 嘗試執行一次靜默登入 (這會使用 store 內的同步鎖)
          const success = await store.dispatch("SilentSignIn");

          if (success) {
            // 刷新成功，更新 token 並重試原始請求
            const newToken = localStorage.getItem("_token");
            config.headers.Authorization = "Bearer " + newToken;
            return instance(config); // 重試
          }

          // 換不到新票，但不是後端說「這張票不能用」（網路斷線、逾時、伺服器 5xx）：
          // 保留登入狀態，這次請求當失敗就好。以前這裡一律登出 —— 網路一閃就要重新登入
          const { lastSilentSignInFailure } = require("@/store");
          if (lastSilentSignInFailure === "transient") {
            error.userMessage = "網路連線異常，請稍後再試。";
            return Promise.reject(error);
          }
          return finalizeUnauthorized(error);
        } catch (retryErr) {
          return finalizeUnauthorized(error);
        }
      }

      error.userMessage = extractErrMsg(error);
      return Promise.reject(error);
    }
  );

  return instance;
};

// 統一處理最後真的失敗的 401 邏輯 (跳轉)
async function finalizeUnauthorized(error) {
  const currentRoute = router.currentRoute.value || router.currentRoute;
  const isAuthRequired = currentRoute.meta && currentRoute.meta.requiresAuth;

  if (!isAuthRequired) {
    console.warn("此頁面無需權限，忽略 401 錯誤。");
    return Promise.reject(error);
  }

  const errorData = error.response?.data;
  const isTokenError = errorData === "RefreshToken過期" || errorData === "RefreshToken已使用" || errorData === "身分驗證失敗";

  if (isTokenError || !localStorage.getItem("_token")) {
    console.error("驗證失效，強制登出並跳轉。原因:", errorData);
    // 要換到登入頁了：開著的登入對話框先關掉，不要疊在登入頁上
    store.commit("CloseLoginDialog");
    await store.dispatch("SignOut", { callApi: false });
    
    if (currentRoute.name !== "SignIn") {
      router.push({
        path: "/signin",
        query: { status: "signIn", token: "expiry" },
      });
    }
  }
  return Promise.reject(error);
}

const normalRequest = createAxiosInstance({});
const logoutRequest = createAxiosInstance({ Logout: "logout" });

// withCredentials: true  瀏覽器才會存取 set-cookies

export const Profile_Get = (email) => normalRequest.get(`Profile/${email}`);
export const Profile_Post = (data) => normalRequest.post(`Profile`, data);
export const Profile_GetReferrerName = (emailCode) => normalRequest.get(`Profile/referrer/${emailCode}`);

export const Advertise_Post = (data) => normalRequest.post(`Advertise`, data);
export const Advertise_Get_Category = () => normalRequest.get(`Advertise/category`);
export const Advertise_Get_Area = () => normalRequest.get(`Advertise/area`);
export const Advertise_Get_Dist = () => normalRequest.get(`Advertise/dist`);
export const Advertise_Get_Questionnaire = () => normalRequest.get(`Advertise/questionnaire`);
export const Advertise_Get_TaroSerivce = () => normalRequest.get(`Advertise/taroSerivce`);
export const Advertise_SemdAdminMail = (data) => normalRequest.post(`Advertise/mail`, data);

export const GetCompanyData = (name, once) => normalRequest.get(`Advertise/company/${name}/${once}`);

export const Advertise_Search = (data) => normalRequest.post(`Advertise/search`, data);

// 自訂頁面（迷你 CMS）—— 公開匿名 API，只回已發佈的快照。
// slug 是完整路徑字串（可含 `/`，如 `yentity/outfit`），逐段 encode，
// 不要整串 encodeURIComponent，否則分隔用的斜線會被編成 %2F。
export const CustomPage_GetBySlug = (slug) => {
  const path = String(slug || "")
    .split("/")
    .filter(Boolean)
    .map(encodeURIComponent)
    .join("/");
  return publicRequest.get(`CustomPage/by-slug/${path}`);
};

// 有自己網域的子站用（例如 www.blackhorn.tw/location）。
//
// host 一定要由前端傳：API 掛在 taro-api 網域下，後端讀到的 Request.Host
// 永遠是 API 自己的網域，不是訪客看到的那一個。
export const CustomPage_GetByHost = (host, path) =>
  publicRequest.get("CustomPage/by-host", {
    params: { host, path: path || "" },
  });

export const CustomPage_GetRedirectByHost = (host, path) =>
  publicRequest.get("CustomPage/by-host/redirect", {
    params: { host, path: path || "" },
  });

// x-product 短碼用。這支後端本來就是 AllowAnonymous，不需要為 CMS 另開 API。
export const Products_GetById = (id) => publicRequest.get(`Products/${id}`);

export const Google_GetSiteToken = (letter) => publicRequest.get(`Google/siteToken/${letter}`);
// app = true：從原生 App 發起，後端登入完成後改導回 App（/app-return/login）。見 utils/nativeApp.js
const googleLoginParams = (app) => (app ? { params: { app: true } } : undefined);
export const Google_Login = (app = false) => publicRequest.get(`Google/googleLogin`, googleLoginParams(app));
export const Google_SignupWithRef = (refc, app = false) => {
  const referral = typeof refc === "string" ? refc.trim() : "";
  return referral
    ? publicRequest.get(`Google/googleLogin/${encodeURIComponent(referral)}`, googleLoginParams(app))
    : Google_Login(app);
};

// 登出與靜默登入使用不帶同步鎖的 authRequest，防止死鎖
// 登出帶著 access token：後端用它認出「這台裝置」，只登出這台（手機、其他瀏覽器不受影響）
export const Token_LogOut = () => authRequest.post(`Token/logout`);
// 逾時一定要設：跨分頁的換票鎖（navigator.locks）會等這個請求結束，卡住的話其他分頁也全部卡住
export const Token_SilentSignIn = () => authRequest.post(`Token/silent-signin`, null, { timeout: 15000 });

export const Token_SignIn = (data) => normalRequest.post(`Token/signin`, data);

// 帳號設定「登入中的裝置」：一次登入＝一台裝置，可以逐台登出或登出這台以外的全部
export const Token_Devices = () => normalRequest.get(`Token/devices`);
export const Token_DeviceLogout = (key) => normalRequest.post(`Token/devices/${encodeURIComponent(key)}/logout`);
// 刪除帳號（2026-10-22，views/Member/AccountDeletion.vue）：狀態與送出前的檢查、Google 帳號的 Email 驗證碼、送出、取消
export const AccountDeletion_Get = () => normalRequest.get(`AccountDeletion`);
export const AccountDeletion_SendCode = () => normalRequest.post(`AccountDeletion/code`);
export const AccountDeletion_Submit = (data) => normalRequest.post(`AccountDeletion`, data);
export const AccountDeletion_Cancel = () => normalRequest.delete(`AccountDeletion`);
export const Token_LogoutOtherDevices = () => normalRequest.post(`Token/devices/logout-others`);
export const Token_Username = () => normalRequest.get(`Token/username`);
export const Token_EmailEncrypt = () => normalRequest.get(`Token/emailEncrypt`);
export const Token_Email = () => normalRequest.get(`Token/email`);
export const Token_Guid = () => normalRequest.get(`Token/guid`);

// 子站自訂網域的登入交接（見 utils/custom-domain-auth.js）
// 領碼要帶登入身分；換碼不能走 normalRequest —— 換不到時不該觸發「401 → 靜默登入重試」那一套
export const Token_HandoffTarget = (url) => publicRequest.get(`Token/handoff/target`, { params: { url } });
export const Token_HandoffCreate = (returnUrl) => normalRequest.post(`Token/handoff`, { returnUrl });
export const Token_HandoffRedeem = (code) => authRequest.post(`Token/handoff/redeem`, { code });

export const Account_GetAccountSetting = (email) => normalRequest.get(`Account/info/${email}`);
export const Account_SignUp = (data) => normalRequest.post(`Account`, data);
// 已登入的會員改自己的密碼（要登入；後端只認登入者本人，data.email 不採用）
export const Account_UpdatePwd = (data) => normalRequest.put(`Account`, data);
// 忘記密碼：用重設信連結上的一次性 token 設定新密碼 { token, password }
export const Account_ResetPwdByToken = (data) => normalRequest.post(`Account/password/reset`, data);

export const Mail_SendResetPwdMail = (email) => normalRequest.post(`Mail/SendResetPwdMail/${email}`);
export const Mail_SendConfirmMail = (data) => normalRequest.post(`Mail/SendConfirmMail`, data);

export const IndustryCategory_Get = () => axios.get("https://marketexploit.kf-9595.com/api/稅務行業標準分類");

export const GetCompanyData_Post = (data) => axios.post("https://marketexploit.kf-9595.com/api/Company", data);

export const GetIndustryVAT = (vat) => axios.get(`https://eip.fia.gov.tw/OAI/api/businessRegistration/${vat}`);

export default {};

export const TokenPurchase_GetProductsByTypeId = (typeId) => normalRequest.get(`TokenPurchase/products/${typeId}`);
export const TokenPurchase_GetTypes = () => normalRequest.get(`TokenPurchase/types`);
export const TokenPurchase_CreateOrder = (dto) => normalRequest.post(`TokenPurchase/order/create`, dto);
export const TokenPurchase_UpdatePayment = (dto) => normalRequest.put(`TokenPurchase/order/payment`, dto);
export const TokenPurchase_GetOrderByNo = (orderNo) => normalRequest.get(`TokenPurchase/order/${orderNo}`);
export const TokenPurchase_GetUserTokenSummary = (email) => normalRequest.get(`TokenPurchase/users/sum/${email}`);
export const TokenPurchase_GetUserToken = (orderNo) => normalRequest.get(`TokenPurchase/users/token/${orderNo}`);

export const TokenPurchase_GetUserPurchasesRecord = (email, page, size, dateFilter, tokenTypeCode, feedFilter) => normalRequest.get(`TokenPurchase/${email}/purchases?p=${page}&ps=${size}&df=${dateFilter}&tt=${tokenTypeCode}&ff=${feedFilter}`);
export const TokenPurchase_GetUserPurchasesRecordSum = (email, tokenCode) => normalRequest.get(`TokenPurchase/${email}/purchases/summary?tt=${tokenCode}`);
export const TokenPurchase_GetTokenLogs = (
  email,
  page,
  size,
  usageLogKinds, // array
  service,
  date
) => {
  const ukParams = usageLogKinds.map((kind) => `uk=${encodeURIComponent(kind)}`).join("&");
  const url = `TokenPurchase/${email}/token-logs` + `?p=${page}&ps=${size}` + `&${ukParams}` + `&sv=${encodeURIComponent(service)}` + `&df=${encodeURIComponent(date)}`;
  return normalRequest.get(url);
};

export const NewebPay_Once = (dto) => normalRequest.post(`NewebPay/once`, dto);

export const Contract_GetList = () => normalRequest.get(`Contract/contract-templates`);
export const Contract_GetById = (id) => normalRequest.get(`Contract/contract-template/${id}`);
export const Contract_Purchaseable = (accountGuid) => normalRequest.get(`Contract/can-purchase?accountGuid=${accountGuid}`);

export const Grade_Purchase = (form) => normalRequest.post(`Grade/purchase`, form);
export const Grade_Apply = (form) => normalRequest.post(`Grade/apply`, form);
export const Grade_CheckEligibility = (ug, tId) => normalRequest.get(`Grade/check?userGuid=${ug}&templateId=${tId}`);

export const Grade_GetUserGradeName = (emailCode) => normalRequest.get(`Grade/name/${emailCode}`);
export const Grade_Reset = (accountGuid) => normalRequest.get(`Grade/reset-latest/${accountGuid}`);
export const Grade_Overview = (accountGuid) => normalRequest.get(`Grade/overview/${accountGuid}`);
export const Grade_UpdateApplication = (id, form) => normalRequest.put(`Grade/application/${id}`, form);
export const Grade_UpdateContractProfile = (contractId, form) => normalRequest.put(`Grade/contract/${contractId}/profile`, form);
export const Grade_GradeRecover = (accountGuid) => normalRequest.post(`Contract/recover?accountGuid=${accountGuid}`);

export const Referral_GetDownlines = (emailCode) => normalRequest.get(`referral/downlines/${emailCode}`);
export const Referral_GetDownlinesCount = (emailCode) => normalRequest.get(`referral/downlines/${emailCode}/count`);

// ===== 行動推播 (FCM) =====
// 註冊裝置推播 token（App 登入後呼叫）
export const DeviceToken_Register = (payload) => normalRequest.post(`DeviceToken/register`, payload);
// 註銷裝置推播 token（登出時）
export const DeviceToken_Unregister = (token) => normalRequest.post(`DeviceToken/unregister`, { token });
// 測試推播：發送一則自訂標題/內容的推播給自己的裝置
export const Push_TestSend = (payload) => normalRequest.post(`DeviceToken/test-send`, payload);

// ===== 統一通知中心 =====
// 通知清單(分頁)；params: { page, pageSize, onlyUnread, source }
export const Notification_GetList = (params) => normalRequest.get(`Notification`, { params });
// 未讀數(鈴鐺紅點輪詢用)
export const Notification_GetUnreadCount = () => normalRequest.get(`Notification/unread-count`);
// 標記單筆已讀
export const Notification_MarkRead = (id) => normalRequest.post(`Notification/${id}/read`);
// 全部標記已讀
export const Notification_MarkAllRead = () => normalRequest.post(`Notification/read-all`);

export const Payment_RechargeAmount = (contractId) => normalRequest.get(`payments/recharge/amount?contractId=${contractId}`);
export const Payment_CancelSubscription = (contractId, alterType) => normalRequest.post(`payments/contract/${contractId}/cancel/${alterType}`);
export const Payment_ReactivateContract = (contractId) => normalRequest.post(`payments/contract/${contractId}/reactivate`);
export const Grade_TestOverdue = (contractId) => normalRequest.post(`grade/test/overdue?contractId=${contractId}`);

// 藍新定期API
export const Payment_Intent = (payload) => normalRequest.post(`payments/intents/`, payload);
export const Payment_GetPayments = (type, page, pageSize, dateFilter) => normalRequest.get(`payments/payments?type=${type}&page=${page}&pageSize=${pageSize}&dateFilter=${dateFilter}`);

export const Product_GetCategories = () => normalRequest.get(`Products/categories`);
export const Product_GetProductsByCateId = (categoryId) => normalRequest.get(`Products/all-by-category?categoryId=${categoryId}`);
export const Product_GetProducts = ({ categoryId, page = 1, pageSize = 12, q, respectCategory = false }) => {
  const params = {
    // 0 或未傳都視為「全部」 → 不帶這個參數
    categoryId: categoryId && categoryId !== 0 ? categoryId : undefined,
    page,
    pageSize,
    q: q && q.trim() ? q.trim() : undefined,
    respectCategory, // 後端預設 false；要強制依分類就傳 true
  };
  return normalRequest.get("Products", { params });
};
export const Product_GetById = (id) => normalRequest.get(`Products/${id}`);
// 商品佣金公告（代理商資源）：分類/關鍵字查詢商品含變體與 UFYC，無限滾動分頁
export const Product_GetCommissionAnnouncement = (accountGuid, { categoryId, q, page = 1, pageSize = 30 } = {}) =>
  normalRequest.get(`Products/commission-announcement/${accountGuid}`, {
    params: {
      categoryId: categoryId && categoryId !== 0 ? categoryId : undefined,
      q: q && q.trim() ? q.trim() : undefined,
      page,
      pageSize,
    },
  });
export const Product_GetDescriptionById = (id) => normalRequest.get(`Products/${id}/description`);
export const Product_ValidateSelection = ({ id, variantId, a, b }) => {
  return normalRequest.get(`Products/${encodeURIComponent(id)}/validate`, {
    params: {
      variantId: variantId || undefined,
      a: a || undefined,
      b: b || undefined,
    },
  });
};

export const GetBuyerProfileByAccount = (accountGuid) => normalRequest.get(`SalesOrder/buyerProfile/${accountGuid}`);
export const GetSalesOrderByOrderNo = (orderNo) => normalRequest.get(`SalesOrder/order/${orderNo}`);
export const GetSalesOrdersByAccountGuid = (accountGuid, page = 1, pageSize = 10, isNewestFirst = true) => normalRequest.get(`SalesOrder/order/all/${accountGuid}`, { params: { page, pageSize, isNewestFirst } });
/*
  組織訂單列表。搜尋、訂閱篩選、排序都交給後端 ——
  這些條件必須在分頁之前套用，前端拿到的只有當前那一頁，自己 filter 是搜不到別頁的。

  opts: { keyword, onlySubscription, sortBy: "created"|"paid", sortDir: "asc"|"desc" }
*/
export const GetSalesOrdersByReferrer = (accountGuid, page, pageSize, opts = {}) =>
  normalRequest.get(`SalesOrder/order/by-referrer/${accountGuid}`, { params: { page, pageSize, ...opts } });
export const GetSalesOrdersByReferrerByYear = (accountGuid, year, page, pageSize, opts = {}) =>
  normalRequest.get(`SalesOrder/order/by-referrer/${accountGuid}/year/${year}`, { params: { page, pageSize, ...opts } });
export const GetSharedOrderDetail = (orderNo) => normalRequest.get(`SalesOrder/order/shared/${orderNo}`);
export const ValidEmail = (email) => normalRequest.get(`SalesOrder/valid-email/${email}`);
export const BatchUpsertMarketingCodes = (data) => normalRequest.post(`SalesOrder/marketing-codes/batch-upsert/`, data);
export const SearchEmailByName = (name) => normalRequest.get(`SalesOrder/search?name=${name}`);

export const SalesOrder_CreateOrder = (fd) => normalRequest.post(`SalesOrder`, fd);
export const SalesOrder_GetMySubscriptions = (accountGuid, page = 1, pageSize = 10, isNewestFirst = true) => normalRequest.get(`SalesOrder/subscriptions/${accountGuid}`, { params: { page, pageSize, isNewestFirst } });
export const SalesOrder_AlterPayPeriodStatus = (data) => normalRequest.post(`SalesOrder/pay/period/alter-status`, data);
export const Commission_Calculate = (lineageString, amount) => normalRequest.get(`Commission/calculate/${lineageString}/${amount}`);
export const Commission_ProcessOrderDemo = (data) => normalRequest.post(`Commission/demo-batch`, data);

// 佣金報表公告（代理商資源）：個人每月佣金報表閱覽與下載
export const Commission_GetReportYears = (accountGuid) => normalRequest.get(`Commission/report/${accountGuid}/years`);
export const Commission_GetMonthlyReports = (accountGuid, year) => normalRequest.get(`Commission/report/${accountGuid}`, { params: { year } });
export const Commission_DownloadReport = (accountGuid, year, month) => normalRequest.get(`Commission/report/${accountGuid}/download`, { params: { year, month }, responseType: "blob" });

export const Account_Count = () => normalRequest.get(`Account/count`);
export const AdminSearch_Names = (email) => normalRequest.get(`AdminSearch/names`, { params: { email } });

// TokenWallet API
export const TokenWallet_Overview = (accountGuid) => normalRequest.get(`TokenWallet/overview/${accountGuid}`);
export const TokenWallet_Earnings = (accountGuid, { page = 1, pageSize = 10, sortDesc = true } = {}) =>
  normalRequest.get(`TokenWallet/earnings/${accountGuid}`, { params: { page, pageSize, sortDesc } });
export const TokenWallet_Settlements = (accountGuid, { page = 1, pageSize = 10, sortDesc = true } = {}) =>
  normalRequest.get(`TokenWallet/settlements/${accountGuid}`, { params: { page, pageSize, sortDesc } });

// 取物件屬性時做個小工具：同時支援 Pascal/Camel
const pick = (obj, ...keys) => keys.reduce((v, k) => (v ??= obj?.[k]), undefined);

// 藍新商品單次支付
export const payOnce = async (obj) => {
  try {
    const resp = await normalRequest.post(`SalesOrder/pay/once`, obj);
    const pd =
      pick(resp?.data, "PaymentData", "paymentData") || // ← 這行是關鍵
      pick(resp, "PaymentData", "paymentData"); // 萬一後端直接回在根

    if (!pd) {
      console.error("payOnce resp.data =", resp?.data);
      throw new Error("藍新金流支付失敗，請聯絡網站管理員。");
    }

    postToNewebpay({
      actionUrl: pd.PostAPI ?? pd.postAPI,
      MerchantID: pd.MerchantID ?? pd.merchantID,
      TradeInfo: pd.TradeInfo ?? pd.tradeInfo,
      TradeSha: pd.TradeSha ?? pd.tradeSha,
      Version: pd.Version ?? pd.version,
    });

    // 新版：只回訂單資訊 -> 交給呼叫端（例如導到訂單完成頁）
    // 原本這裡寫 order: data，但 data 未宣告會丟 ReferenceError —— 表單已送出、
    // 頁面正在導向藍新，錯誤卻被下面的 catch 接住變成「支付失敗」訊息。
    return { mode: "created", order: resp?.data };
  } catch (err) {
    // 友善錯誤
    const msg = err.userMessage || "藍新金流支付失敗，請聯絡網站管理員。";
    // 你可以在這裡彈 toast
    // this.$toast.error(msg);
    throw new Error(msg);
  }
};

// 藍新定期定額支付
export const paySubscription = async (obj) => {
  try {
    const resp = await normalRequest.post(`SalesOrder/pay/period`, obj);
    const pd = pick(resp?.data, "PaymentData", "paymentData");

    if (!pd) {
      console.error("paySubscription resp.data =", resp?.data);
      throw new Error("藍新金流支付失敗，請聯絡網站管理員。");
    }

    postToNewebpay({
      actionUrl: pd.PostAPI ?? pd.postAPI,
      MerchantID_: pd.MerchantID_ ?? pd.merchantID_,
      PostData_: pd.PostData_ ?? pd.postData_,
    });

    // 新版：只回訂單資訊（同 payOnce：原本誤用未宣告的 data）
    return { mode: "created", order: resp?.data };
  } catch (err) {
    const msg = err.userMessage || "藍新定期支付失敗，請聯絡網站管理員。";
    throw new Error(msg);
  }
};
